import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatError, resolveImage } from "../../lib/api";
import { Shell } from "./_shell";
import { ImagePicker, ConfirmDialog } from "./_shared";

const EMPTY_CAT = { name: "", slug: "", description: "", image_url: "", status: "available", section: "regular", display_order: 0 };

export default function Categories() {
  const [list, setList] = useState([]);
  const [productCounts, setProductCounts] = useState({}); // {slug: count}
  const [form, setForm] = useState(EMPTY_CAT);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [{ data: cats }, { data: prods }] = await Promise.all([
        api.get("/categories"), api.get("/products"),
      ]);
      setList(cats);
      const counts = {};
      for (const p of prods) counts[p.category_slug] = (counts[p.category_slug] || 0) + 1;
      setProductCounts(counts);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name || !form.slug) { toast.error("Name and slug required"); return; }
    try {
      const payload = { ...form, display_order: parseInt(form.display_order) || 0 };
      if (editId) await api.put(`/categories/${editId}`, payload);
      else await api.post("/categories", payload);
      toast.success(editId ? "Category updated" : "Category created");
      setForm(EMPTY_CAT); setEditId(null); setShowForm(false); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const doDelete = async () => {
    if (!confirmDel) return;
    setBusy(true);
    try { await api.delete(`/categories/${confirmDel.id}`); toast.success(`Deleted "${confirmDel.name}"`); setConfirmDel(null); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const edit = (c) => { setEditId(c.id); setForm({ ...EMPTY_CAT, ...c }); setShowForm(true); window.scrollTo({ top: 0, behavior: "smooth" }); };

  return (
    <Shell active="categories">
      <div className="p-5 md:p-10">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
          <div>
            <div className="overline mb-2">Catalogue structure</div>
            <h1 className="font-display text-4xl md:text-6xl uppercase leading-none">Categories.</h1>
          </div>
          <button onClick={() => { setEditId(null); setForm(EMPTY_CAT); setShowForm(s => !s); }} className="btn-primary" data-testid="btn-add-category">
            {showForm ? "Close" : "Add category"}
          </button>
        </div>

        {showForm && (
          <div className="border border-ink hard-shadow p-6 mb-8 bg-paper" data-testid="category-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="overline block mb-2">Name *</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value, slug: editId ? form.slug : e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "") })} className="field" data-testid="cf-name" />
              </div>
              <div>
                <label className="overline block mb-2">Slug *</label>
                <input value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9_]+/g, "_") })} className="field" data-testid="cf-slug" />
              </div>
              <div className="md:col-span-2">
                <label className="overline block mb-2">Description</label>
                <textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="field" data-testid="cf-desc" />
              </div>
              <div className="md:col-span-2">
                <label className="overline block mb-2">Category image</label>
                <ImagePicker value={form.image_url} onChange={v => setForm({ ...form, image_url: v })} testid="cf-image" label="category image" />
              </div>
              <div>
                <label className="overline block mb-2">Section</label>
                <select value={form.section} onChange={e => setForm({ ...form, section: e.target.value })} className="field" data-testid="cf-section">
                  <option value="regular">Regular Catalog</option>
                  <option value="imported">Imported</option>
                </select>
              </div>
              <div>
                <label className="overline block mb-2">Status</label>
                <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className="field" data-testid="cf-status">
                  <option value="available">Available</option>
                  <option value="coming_soon">Coming Soon</option>
                  <option value="hidden">Hidden</option>
                </select>
              </div>
              <div>
                <label className="overline block mb-2">Display order</label>
                <input type="number" value={form.display_order} onChange={e => setForm({ ...form, display_order: e.target.value })} className="field" data-testid="cf-order" />
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <button onClick={save} className="btn-primary" data-testid="cf-save">{editId ? "Save changes" : "Add category"}</button>
              <button onClick={() => { setEditId(null); setShowForm(false); setForm(EMPTY_CAT); }} className="btn-ghost">Cancel</button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="categories-grid">
          {list.map(c => (
            <div key={c.id} className="border border-ink p-4 flex gap-3 bg-paper" data-testid={`admin-category-${c.id}`}>
              <div className="w-20 h-20 border border-ink overflow-hidden shrink-0 bg-secondary">
                {c.image_url ? <img src={resolveImage(c.image_url)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[10px] text-black/40">—</div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap mb-1">
                  <span className={`chip text-[9px] ${c.status === "available" ? "" : c.status === "coming_soon" ? "text-soda border-soda" : "text-black/40 border-black/30"}`}>{c.status.replace("_", " ")}</span>
                  <span className="chip text-[9px]">{c.section}</span>
                  <span className="chip text-[9px] text-black/50">sort {c.display_order}</span>
                  {productCounts[c.slug] > 0 && <span className="chip text-[9px] text-cobalt border-cobalt" data-testid={`cat-prod-count-${c.id}`}>{productCounts[c.slug]} products</span>}
                </div>
                <div className="font-bold text-sm leading-tight">{c.name}</div>
                <div className="text-xs text-black/60 mt-1">/{c.slug}</div>
                {c.description && <div className="text-xs text-black/60 mt-1 line-clamp-2">{c.description}</div>}
                <div className="mt-2 flex gap-3">
                  <button onClick={() => edit(c)} className="text-xs underline" data-testid={`edit-cat-${c.id}`}>Edit</button>
                  <button onClick={() => setConfirmDel(c)} className="text-xs underline text-soda" data-testid={`delete-cat-${c.id}`}>Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <ConfirmDialog
        open={!!confirmDel}
        title={`Delete category${confirmDel ? `: ${confirmDel.name}` : ""}?`}
        body={confirmDel && productCounts[confirmDel.slug] > 0
          ? `⚠️ ${productCounts[confirmDel.slug]} product(s) still use this category. They will remain but show an unknown category on the public catalog.\n\nReassign them to another category first, or delete this anyway.`
          : `This permanently removes the "${confirmDel?.name || ""}" category. No products currently use it, so this is safe.`}
        confirmLabel={confirmDel && productCounts[confirmDel.slug] > 0 ? "Delete anyway" : "Delete category"}
        danger busy={busy}
        onConfirm={doDelete} onCancel={() => setConfirmDel(null)}
        testid="category-delete-dialog"
      />
    </Shell>
  );
}
